import { MessageBar, MessageBarBody } from '@fluentui/react-components';
import { CheckmarkCircle20Regular, ThumbLike20Regular } from '@fluentui/react-icons';
import { useT } from '@powerportalspro/react';
import {
  GridToolbarItems,
  MainGrid,
  OperationGridButton,
  type OperationCompleted,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';
import { useState } from 'react';

// OperationGridButton over the demo's accounts. Check runs the demo's own code on each selected
// account — registered for everyone, with each account one the user may read
// (OnRecord("Target", TableSecurityPermission.Read)) — and lists the names it answers. Approve is
// granted only to the "Demo Approvers" portal role, which no visitor holds: asked to, it shows
// disabled rather than hidden. Mirrors the Blazor `OperationGridButtonDemo`.

const STRINGS_BASE = 'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Common.OperationGridButtonDemo';
const ALL_ACCOUNTS_VIEW_ID = '91732AD4-B4FE-49FF-80CD-72B280EFF088';

const ALL_ACCOUNTS_VIEW: ViewDefinition = {
  id: ALL_ACCOUNTS_VIEW_ID,
  tableName: 'account',
  displayName: 'Active Accounts',
  fetchXml: `<fetch>
    <entity name="account">
      <attribute name="name" />
      <attribute name="accountnumber" />
      <attribute name="telephone1" />
      <attribute name="address1_city" />
      <order attribute="name" />
    </entity>
  </fetch>`,
  columns: [
    { columnName: 'name', width: 200 },
    { columnName: 'accountnumber', width: 160 },
    { columnName: 'telephone1', width: 160 },
    { columnName: 'address1_city', width: 160 },
  ],
};

export function OperationGridButtonSample() {
  const t = useT();
  const [checkedNames, setCheckedNames] = useState<string>();

  const checked = ({ responses }: OperationCompleted) => {
    const names = responses
      .map((x) => ((x as { outputParameters?: Record<string, { value?: string }> }).outputParameters?.Name?.value ?? '').trim())
      .filter((x) => x.length > 0);
    setCheckedNames(t(`${STRINGS_BASE}.checked`, [names.join(', ')]));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacingVerticalM)' }}>
      <MainGrid
        tableName="account"
        minHeight="400px"
        maxHeight="400px"
        customViewDefinitions={[ALL_ACCOUNTS_VIEW]}
        viewIds={[ALL_ACCOUNTS_VIEW_ID]}
        defaultViewId={ALL_ACCOUNTS_VIEW_ID}
      >
        <GridToolbarItems>
          <OperationGridButton
            operation="DemoCheckAccount"
            recordParameter="Target"
            label={t(`${STRINGS_BASE}.check-label`)}
            icon={<CheckmarkCircle20Regular />}
            onCompleted={checked}
          />
          <OperationGridButton
            operation="DemoApproveAccount"
            recordParameter="Target"
            label={t(`${STRINGS_BASE}.approve-label`)}
            icon={<ThumbLike20Regular />}
            tooltip={t(`${STRINGS_BASE}.approve-tooltip`)}
            whenNotAllowed="disable"
          />
        </GridToolbarItems>
      </MainGrid>
      {checkedNames && (
        <MessageBar intent="success">
          <MessageBarBody>{checkedNames}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
